import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import ProfessionalDashboardNavbar from 'pages/professional-dashboard/components/ProfessionalDashboardNavbar';
import Footer from 'pages/landing-page/components/Footer';
import { loadStripe } from '@stripe/stripe-js';
import { ArrowLeft, CreditCard, AlertTriangle, CheckCircle, XCircle, Loader2, ShoppingCart, Clock, Tag } from 'lucide-react';
import Icon from '../../components/AppIcon';


const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'success' ? '#dcfce7' : '#fee2e2';
  const border = type === 'success' ? '#86efac' : '#fca5a5';
  const text = type === 'success' ? '#166534' : '#991b1b';
  const Icon = type === 'success' ? CheckCircle : XCircle;

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: '24px',
        zIndex: 9999,
        maxWidth: '380px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '12px',
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      }}
    >
      <Icon size={18} color={text} style={{ flexShrink: 0, marginTop: '1px' }} />
      <p style={{ color: text, fontSize: '14px', lineHeight: '1.5', margin: 0 }}>{message}</p>
      <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: text, flexShrink: 0 }}>✕</button>
    </div>
  );
};

// ─── Balance Section ──────────────────────────────────────────────────────────
const BalanceSection = ({ balance, loading }) => {
  const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return `${String(d?.getDate())?.padStart(2, '0')}/${String(d?.getMonth() + 1)?.padStart(2, '0')}/${d?.getFullYear()}`;
  };

  const showWarning = !loading && balance?.credits_balance === 0 && balance?.free_quotes_remaining === 0;

  return (
    <div style={{
      background: '#fff',
      borderRadius: '16px',
      border: '1px solid #e5e7eb',
      padding: '28px 32px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <CreditCard size={20} color="#104d98" />
        Saldo attuale
      </h2>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6b7280' }}>
          <Loader2 size={18} className="animate-spin" />
          <span style={{ fontSize: '14px' }}>Caricamento saldo...</span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Credits balance */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Crediti carnet disponibili:</span>
            <span style={{ fontSize: '36px', fontWeight: 800, color: '#32b3e5', lineHeight: 1 }}>
              {balance?.credits_balance ?? 0}
            </span>
          </div>

          {/* Expiry */}
          {balance?.credits_expires_at && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={14} color="#9ca3af" />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                Scadono il: <strong style={{ color: '#374151' }}>{formatDate(balance?.credits_expires_at)}</strong>
              </span>
            </div>
          )}

          {/* Free quotes */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>
              Preventivi gratuiti rimanenti: <strong style={{ color: '#6b7280' }}>{balance?.free_quotes_remaining ?? 0}</strong>
            </span>
          </div>

          {/* Warning */}
          {showWarning && (
            <div style={{
              marginTop: '8px',
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '10px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}>
              <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                Hai esaurito i crediti gratuiti. Ogni preventivo accettato costerà <strong>€0,99</strong> a fine mese.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Package Card ─────────────────────────────────────────────────────────────
const PackageCard = ({ pkg, onPurchase, purchasing }) => {
  const price = ((pkg?.price_cents ?? 0) / 100)?.toFixed(2)?.replace('.', ',');
  const isBuying = purchasing === pkg?.id;

  return (
    <div style={{
      background: '#fff',
      borderRadius: '14px',
      border: '2px solid #e5e7eb',
      padding: '24px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      position: 'relative',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      flex: '1 1 0',
      minWidth: '200px',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#104d98'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(16,77,152,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Discount badge */}
      {(pkg?.discount_pct ?? 0) > 0 && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '16px',
          background: '#104d98',
          color: '#fff',
          fontSize: '11px',
          fontWeight: 700,
          padding: '3px 10px',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <Tag size={10} />
          Sconto {pkg?.discount_pct}%
        </div>
      )}

      {/* Name */}
      <p style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: 0 }}>{pkg?.name}</p>

      {/* Credits */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
        <span style={{ fontSize: '42px', fontWeight: 800, color: '#32b3e5', lineHeight: 1 }}>{pkg?.credits}</span>
        <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 500 }}>crediti</span>
      </div>

      {/* Price */}
      <p style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0 }}>€{price}</p>

      {/* Validity */}
      <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
        Validità: {pkg?.validity_days ?? 0} giorni
      </p>

      {/* Buy button */}
      <button
        onClick={() => onPurchase(pkg)}
        disabled={isBuying}
        style={{
          marginTop: '8px',
          background: isBuying ? '#93c5fd' : '#104d98',
          color: '#fff',
          border: 'none',
          borderRadius: '10px',
          padding: '10px 0',
          fontSize: '14px',
          fontWeight: 700,
          cursor: isBuying ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'background 0.2s',
          width: '100%',
        }}
        onMouseEnter={e => { if (!isBuying) e.currentTarget.style.background = '#0d3d7a'; }}
        onMouseLeave={e => { if (!isBuying) e.currentTarget.style.background = '#104d98'; }}
      >
        {isBuying ? (
          <><Loader2 size={15} className="animate-spin" /> Elaborazione...</>
        ) : (
          <><ShoppingCart size={15} /> Acquista</>
        )}
      </button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ProfessionalCrediti = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast(null);

  // ── Fetch balance ──────────────────────────────────────────────────────────
  const fetchBalance = useCallback(async () => {
    if (!user?.id) return;
    setBalanceLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('users')
        ?.select('credits_balance, credits_expires_at, free_quotes_remaining')
        ?.eq('id', user?.id)
        ?.single();
      if (!error && data) setBalance(data);
    } catch (err) {
      console.error('fetchBalance error:', err);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.id]);

  // ── Fetch packages ─────────────────────────────────────────────────────────
  const fetchPackages = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('credit_packages')
        ?.select('*')
        ?.eq('active', true)
        ?.order('sort_order', { ascending: true });
      if (!error && data) setPackages(data);
    } catch (err) {
      console.error('fetchPackages error:', err);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  // ── Fetch ledger ───────────────────────────────────────────────────────────
  const fetchLedger = useCallback(async () => {
    if (!user?.id) return;
    setLedgerLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('credits_ledger')
        ?.select('*')
        ?.eq('professional_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (!error && data) setLedger(data);
    } catch (err) {
      console.error('fetchLedger error:', err);
    } finally {
      setLedgerLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBalance();
    fetchPackages();
    fetchLedger();
  }, [fetchBalance, fetchPackages, fetchLedger]);

  // ── Purchase handler ───────────────────────────────────────────────────────
  const handlePurchase = async (pkg) => {
    if (!user) return;
    setPurchasing(pkg?.id);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const token = sessionData?.session?.access_token;

      const { data, error } = await supabase?.functions?.invoke('purchase-carnet', {
        body: { package_id: pkg?.id },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (error) {
        showToast(error?.message || 'Errore durante l\'acquisto.', 'error');
        return;
      }

      if (data?.status === 'succeeded') {
        showToast('Crediti aggiunti! Ricarica la pagina per vedere il saldo aggiornato.');
        await fetchBalance();
        await fetchLedger();
        return;
      }

      if (data?.client_secret) {
        const stripe = await stripePromise;
        if (!stripe) {
          showToast('Stripe non disponibile. Riprova.', 'error');
          return;
        }
        const { error: stripeError, paymentIntent } = await stripe?.confirmCardPayment(data?.client_secret);
        if (stripeError) {
          showToast(stripeError?.message || 'Pagamento non riuscito.', 'error');
          return;
        }
        if (paymentIntent?.status === 'succeeded') {
          showToast('Crediti aggiunti! Ricarica la pagina per vedere il saldo aggiornato.');
          await fetchBalance();
          await fetchLedger();
        }
        return;
      }

      // Fallback error
      const errMsg = data?.error || data?.message || 'Risposta imprevista dal server.';
      showToast(errMsg, 'error');
    } catch (err) {
      showToast(err?.message || 'Errore imprevisto.', 'error');
    } finally {
      setPurchasing(null);
    }
  };

  // ── Ledger helpers ─────────────────────────────────────────────────────────
  const formatTipo = (type) => {
    if (type === 'purchase') return 'Acquisto carnet';
    if (type === 'consume') return 'Preventivo inviato';
    if (type === 'admin_grant') return 'Bonus admin';
    return type ?? '—';
  };

  const formatDelta = (delta) => {
    if (delta == null) return '—';
    return delta > 0 ? `+${delta}` : `${delta}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${String(d?.getDate())?.padStart(2, '0')}/${String(d?.getMonth() + 1)?.padStart(2, '0')}/${d?.getFullYear()}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <ProfessionalDashboardNavbar />
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={hideToast} />}
      <main style={{ flex: 1, maxWidth: '960px', margin: '0 auto', width: '100%', padding: '40px 20px 60px' }}>

        {/* Page title */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#111827', margin: 0 }}>I miei crediti</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '6px' }}>Gestisci il tuo saldo crediti e acquista nuovi carnet</p>
        </div>

        {/* ── SECTION 1: Balance ── */}
        <BalanceSection balance={balance} loading={balanceLoading} />

        {/* ── SECTION 2: Purchase ── */}
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          padding: '28px 32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginTop: '24px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShoppingCart size={20} color="#104d98" />
            Acquista un carnet
          </h2>

          {packagesLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6b7280' }}>
              <Loader2 size={18} className="animate-spin" />
              <span style={{ fontSize: '14px' }}>Caricamento pacchetti...</span>
            </div>
          ) : packages?.length === 0 ? (
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>Nessun pacchetto disponibile al momento.</p>
          ) : (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {packages?.map((pkg) => (
                <PackageCard
                  key={pkg?.id}
                  pkg={pkg}
                  onPurchase={handlePurchase}
                  purchasing={purchasing}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── SECTION 3: Ledger ── */}
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          padding: '28px 32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginTop: '24px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>
            Storico movimenti
          </h2>

          {ledgerLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#6b7280' }}>
              <Loader2 size={18} className="animate-spin" />
              <span style={{ fontSize: '14px' }}>Caricamento storico...</span>
            </div>
          ) : ledger?.length === 0 ? (
            <p style={{ fontSize: '14px', color: '#9ca3af' }}>Nessun movimento ancora.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    {['Data', 'Tipo', 'Crediti', 'Scadenza']?.map((col) => (
                      <th key={col} style={{ textAlign: 'left', padding: '10px 12px', color: '#6b7280', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ledger?.map((row, i) => {
                    const delta = row?.delta ?? row?.credits_delta ?? row?.amount;
                    const isPositive = (delta ?? 0) > 0;
                    return (
                      <tr key={row?.id ?? i} style={{ borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 12px', color: '#374151' }}>{formatDate(row?.created_at)}</td>
                        <td style={{ padding: '12px 12px', color: '#374151' }}>{formatTipo(row?.type ?? row?.transaction_type)}</td>
                        <td style={{ padding: '12px 12px', fontWeight: 700, color: isPositive ? '#059669' : '#dc2626' }}>
                          {formatDelta(delta)}
                        </td>
                        <td style={{ padding: '12px 12px', color: '#6b7280' }}>{formatDate(row?.expires_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Back link ── */}
        <div style={{ marginTop: '32px' }}>
          <Link
            to="/professional-dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: '#104d98',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
          >
            <ArrowLeft size={16} />
            Torna alla dashboard
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProfessionalCrediti;
